import {
  DEFAULT_LOCALE_PREFERENCES,
  SUPPORTED_LOCALE_LANGUAGES,
  type LocaleLanguage,
  type LocalePreferences,
  type TimezoneOverride,
} from '@molt/shared';
import { type ChangeEvent, type ReactElement } from 'react';
import { Notice, Skeleton } from '@/components';
import {
  formatLocaleDateTimeLabel,
  languageLabel,
  timezoneLabel,
} from '@/lib/locale-formatting';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface LocalePreferencesSectionProps {
  preferences: LocalePreferences | null;
  loading?: boolean;
  error?: string | null;
  languageBusy?: boolean;
  timezoneBusy?: boolean;
  languageError?: string | null;
  timezoneError?: string | null;
  onLanguageChange: (language: LocaleLanguage) => void;
  onTimezoneChange: (timezoneOverride: TimezoneOverride) => void;
}

const TIMEZONE_OPTIONS = [
  null,
  'UTC',
  'Europe/Chisinau',
  'America/New_York',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Tokyo',
] as const satisfies readonly TimezoneOverride[];

const PREVIEW_INSTANT = '2026-04-03T09:00:00.000Z';

export function LocalePreferencesSection({
  preferences,
  loading = false,
  error = null,
  languageBusy = false,
  timezoneBusy = false,
  languageError = null,
  timezoneError = null,
  onLanguageChange,
  onTimezoneChange,
}: LocalePreferencesSectionProps): ReactElement {
  // FE-80.1 — the six chrome values this comment used to defer ('Language and
  // time', 'Interface language', 'Timezone', 'Date preview', the loading label
  // and the error title) now route through `profile.locale.*`, taken by the
  // profile-page slice this file named. `t` still reaches the three
  // `lib/locale-formatting` leaves as well; nothing in this file is English any
  // more except the raw IANA identifiers in `TIMEZONE_OPTIONS`, which are
  // identifiers rather than copy and stay literal in every locale.
  const { t } = useInterfaceLanguage();
  const data = preferences ?? DEFAULT_LOCALE_PREFERENCES;

  function handleLanguageChange(event: ChangeEvent<HTMLSelectElement>) {
    onLanguageChange(event.currentTarget.value as LocaleLanguage);
  }

  function handleTimezoneChange(event: ChangeEvent<HTMLSelectElement>) {
    onTimezoneChange(event.currentTarget.value === '' ? null : event.currentTarget.value);
  }

  return (
    <section aria-labelledby="language-time-heading" className="flex flex-col gap-3">
      <h2 id="language-time-heading" className="text-base font-semibold text-ink">
        {t('profile.locale.heading')}
      </h2>
      <div className="rounded-card border border-border-strong bg-surface-raised p-4">
        {loading ? (
          <div aria-label={t('profile.locale.loading')} aria-busy="true">
            <Skeleton shape="row" count={2} />
          </div>
        ) : error !== null ? (
          <Notice tone="bad" title={t('profile.locale.loadFailedTitle')} body={error} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-ink">
              <span className="font-medium">{t('profile.locale.interfaceLanguage')}</span>
              <select
                value={data.language}
                disabled={languageBusy}
                onChange={handleLanguageChange}
                className="min-h-9 rounded-card border border-border-strong bg-surface px-2 text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {SUPPORTED_LOCALE_LANGUAGES.map((language) => (
                  <option key={language} value={language}>
                    {languageLabel(t, language)}
                  </option>
                ))}
              </select>
              {languageError !== null ? (
                <span className="text-xs text-bad">{languageError}</span>
              ) : null}
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink">
              <span className="font-medium">{t('profile.locale.timezone')}</span>
              <select
                value={data.timezoneOverride ?? ''}
                disabled={timezoneBusy}
                onChange={handleTimezoneChange}
                className="min-h-9 rounded-card border border-border-strong bg-surface px-2 text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {TIMEZONE_OPTIONS.map((timezone) => (
                  <option key={timezone ?? 'device'} value={timezone ?? ''}>
                    {timezoneLabel(t, timezone)}
                  </option>
                ))}
              </select>
              {timezoneError !== null ? (
                <span className="text-xs text-bad">{timezoneError}</span>
              ) : null}
            </label>
            <div className="sm:col-span-2">
              <span className="block text-sm font-medium text-ink">
                {t('profile.locale.datePreview')}
              </span>
              <p className="text-sm text-ink-muted">
                {formatLocaleDateTimeLabel(t, PREVIEW_INSTANT, data)}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
